import React, { forwardRef, useImperativeHandle, type JSX } from 'react';
import { Flex, FlexColumn, Group, Note, Title } from '..';
import { useVisibilityChange } from '../utils';
import { TimelineItem } from './TimelineItem.component';

export interface IStepProps {
  size?: 's' | 'l';
  currentStep?: string;
  title: string | JSX.Element;
  menuTitle?: React.ReactNode;
  note?: string;
  hint?: JSX.Element;
  subject?: string;
  children?: React.ReactNode;
  stepRef?: (el: HTMLDivElement | null) => void;
  onVisibilityChanged?: (isVisible: boolean) => void;
  ref?: React.Ref<StepHandle>;
}

export type StepHandle = {
  jumpToStep: () => void;
};

export const Step = forwardRef<StepHandle, IStepProps>(
  ({ size = 'l', currentStep, title, note, children, subject, hint, onVisibilityChanged = () => {} }, ref) => {
    const elementRef = useVisibilityChange(onVisibilityChanged);

    useImperativeHandle(
      ref,
      () => ({
        jumpToStep: () => {
          elementRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        },
      }),
      [],
    );

    return (
      <TimelineItem size={size} text={currentStep} innerRef={elementRef} backgroundColor={'blue.800'}>
        <Group gap={5} subject={subject} vertical width={'100%'} fullHeight>
          <Flex justifyContent={'space-between'} width={'100%'}>
            <Group gap={4}>
              {typeof title === 'string' ? <Title size={size === 'l' ? 20 : 14}>{title}</Title> : title}
              {hint}
              {note && (
                <Note type="status" variant="info">
                  {note}
                </Note>
              )}
            </Group>
          </Flex>
          <FlexColumn width={'100%'} height={'100%'}>
            <Group width={'100%'} flexGrow={1}>
              {children}
            </Group>
          </FlexColumn>
        </Group>
      </TimelineItem>
    );
  },
);

Step.displayName = 'Step';
